/**
 * i18n 主入口文件
 * 
 * 国际化配置和初始化
 * 使用模块化翻译结构
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English translations
import { enCommon } from './locales/en/common';
import { enHome } from './locales/en/home';
import { enProducts } from './locales/en/products';
import { enTechnology } from './locales/en/technology';
import { enAbout } from './locales/en/about';
import { enContact } from './locales/en/contact';
import { enLegal } from './locales/en/legal';
import { enFeatures } from './locales/en/features';
import { enQuote } from './locales/en/quote';
import { enFaq } from './locales/en/faq';

// Import Chinese translations
import { zhCommon } from './locales/zh/common';
import { zhHome } from './locales/zh/home';
import { zhProducts } from './locales/zh/products';
import { zhTechnology } from './locales/zh/technology';
import { zhAbout } from './locales/zh/about';
import { zhContact } from './locales/zh/contact';
import { zhLegal } from './locales/zh/legal';
import { zhFeatures } from './locales/zh/features';
import { zhQuote } from './locales/zh/quote';
import { zhFaq } from './locales/zh/faq';

// Import Arabic translations
import { arCommon } from './locales/ar/common';
import { arHome } from './locales/ar/home';
import { arProducts } from './locales/ar/products';
import { arTechnology } from './locales/ar/technology';
import { arAbout } from './locales/ar/about';
import { arContact } from './locales/ar/contact';
import { arLegal } from './locales/ar/legal';
import { arFeatures } from './locales/ar/features';
import { arQuote } from './locales/ar/quote';
import { arFaq } from './locales/ar/faq';

// Import Spanish translations
import { esCommon } from './locales/es/common';
import { esHome } from './locales/es/home';
import { esProducts } from './locales/es/products';
import { esTechnology } from './locales/es/technology';
import { esAbout } from './locales/es/about';
import { esContact } from './locales/es/contact';
import { esLegal } from './locales/es/legal';
import { esFeatures } from './locales/es/features';
import { esQuote } from './locales/es/quote';
import { esFaq } from './locales/es/faq';

// Import Portuguese translations
import { ptCommon } from './locales/pt/common';
import { ptHome } from './locales/pt/home';
import { ptProducts } from './locales/pt/products';
import { ptTechnology } from './locales/pt/technology';
import { ptAbout } from './locales/pt/about';
import { ptContact } from './locales/pt/contact';
import { ptLegal } from './locales/pt/legal';
import { ptFeatures } from './locales/pt/features';
import { ptQuote } from './locales/pt/quote';
import { ptFaq } from './locales/pt/faq';

// Import French translations
import { frCommon } from './locales/fr/common';
import { frHome } from './locales/fr/home';
import { frProducts } from './locales/fr/products';
import { frTechnology } from './locales/fr/technology';
import { frAbout } from './locales/fr/about';
import { frContact } from './locales/fr/contact';
import { frLegal } from './locales/fr/legal';
import { frFeatures } from './locales/fr/features';
import { frQuote } from './locales/fr/quote';
import { frFaq } from './locales/fr/faq';

// Import Russian translations
import { ruModules } from './locales/ru';

// Merge utilities
import { createLanguageResource } from './utils/merge';
import { showcaseResources } from './showcase';
import { technologyStoryResources } from './technologyStory';
import { installationsPageResources } from './installationsPage';
import { merchandisingResources } from './merchandising';

// Create language resources by merging modules
const enResources = createLanguageResource({
  common: enCommon,
  home: enHome,
  products: enProducts,
  technology: enTechnology,
  about: enAbout,
  contact: enContact,
  legal: enLegal,
  features: enFeatures,
  quote: enQuote,
  faq: enFaq,
});

const zhResources = createLanguageResource({
  common: zhCommon,
  home: zhHome,
  products: zhProducts,
  technology: zhTechnology,
  about: zhAbout,
  contact: zhContact,
  legal: zhLegal,
  features: zhFeatures,
  quote: zhQuote,
  faq: zhFaq,
});

const arResources = createLanguageResource({
  common: arCommon,
  home: arHome,
  products: arProducts,
  technology: arTechnology,
  about: arAbout,
  contact: arContact,
  legal: arLegal,
  features: arFeatures,
  quote: arQuote,
  faq: arFaq,
});

const esResources = createLanguageResource({
  common: esCommon,
  home: esHome,
  products: esProducts,
  technology: esTechnology,
  about: esAbout,
  contact: esContact,
  legal: esLegal,
  features: esFeatures,
  quote: esQuote,
  faq: esFaq,
});

const ptResources = createLanguageResource({
  common: ptCommon,
  home: ptHome,
  products: ptProducts,
  technology: ptTechnology,
  about: ptAbout,
  contact: ptContact,
  legal: ptLegal,
  features: ptFeatures,
  quote: ptQuote,
  faq: ptFaq,
});

const frResources = createLanguageResource({
  common: frCommon,
  home: frHome,
  products: frProducts,
  technology: frTechnology,
  about: frAbout,
  contact: frContact,
  legal: frLegal,
  features: frFeatures,
  quote: frQuote,
  faq: frFaq,
});

const ruResources = createLanguageResource(ruModules);

for (const [language, resource] of Object.entries({ en: enResources, zh: zhResources, ar: arResources, es: esResources, pt: ptResources, fr: frResources, ru: ruResources })) {
  resource.translation.showcase = showcaseResources[language];
  resource.translation.technologyStory = technologyStoryResources[language];
  resource.translation.installationsPage = installationsPageResources[language];
  resource.translation.merchandising = merchandisingResources[language];
}

// Configure i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: enResources,
      zh: zhResources,
      ar: arResources,
      es: esResources,
      pt: ptResources,
      fr: frResources,
      ru: ruResources,
    },
    supportedLngs: ['en', 'zh', 'ar', 'es', 'pt', 'fr', 'ru'],
    nonExplicitSupportedLngs: true,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['querystring', 'localStorage'],
      lookupQuerystring: 'lng',
      caches: ['localStorage'],
    },
  });

/**
 * Keep the document direction in sync with the active language.
 * Arabic renders right-to-left; everything else is left-to-right.
 * Tailwind rtl:/ltr: variants and CSS logical properties key off this attribute.
 */
const RTL_LANGS = ['ar'];
function applyDocumentDirection(lng: string) {
  const lang = (lng || 'en').split('-')[0];
  const dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
  if (typeof document !== 'undefined') {
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }
}
i18n.on('languageChanged', applyDocumentDirection);
applyDocumentDirection(i18n.resolvedLanguage || i18n.language);

export default i18n;
